<template>
  <validation-observer v-if="isReady" ref="updateEvent" #default="{ invalid }">
    <n-form-confirmation
      key="updateEvent"
      @submit="validateForm"
      :form="$refs.updateEvent"
      :bypass="true"
      :disabled="invalid || !formModel.timeShift"
    >
      <div class="d-flex justify-content-between">
        <b-card-title class="mb-1">{{ displayDate }}</b-card-title>
        <b-dropdown
          v-if="!isNew"
          style="width: 2.5rem; margin-right: -0.5rem"
          variant="link"
          no-caret
          toggle-class="p-0"
          right
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="17"
              class="align-middle text-body"
            />
          </template>
          <b-dropdown-item v-if="isSingle" @click="handleDelete">
            {{ $t('button.delete') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!isSingle" @click="handleDeleteOne">
            {{ $t('button.deleteOnlyThisDate') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!isRoot" @click="handleDeleteFrom">
            {{ $t('button.deleteAllFutureDates') }}
          </b-dropdown-item>
          <b-dropdown-item v-if="!isSingle" @click="handleDelete">
            {{ $t('button.deleteAll') }}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <n-input :fields="fields" v-model="formModel" />

      <div
        class="mb-1 cursor-pointer text-primary"
        @click="handleEditCustomRepeat"
        v-if="eventModel.isCustom"
      >
        {{ $t('label.yourCustomRepeatOptions') }}
      </div>

      <n-input
        v-if="repeatType !== 'none'"
        :fields="fields__EndRepeat"
        v-model="formModel__EndRepeat"
      />

      <!-- Bootstrap Popover -- Create New Time-shift -->
      <b-popover
        :show="showTimeShiftModal"
        triggers="manual"
        target="timeShift"
        placement="right"
      >
        <div style="width: 350px">
          <time-shift-form
            :form-title="$t('breadcrumb.createTimeShift')"
            @success="onAddedTimeShift"
            @hidden="onTimeShiftModalClose"
          />
        </div>
      </b-popover>

      <!-- Bootstrap Popover -- Custom Repeat -->
      <b-popover
        :show="showCustom"
        triggers="manual"
        target="repeatType"
        placement="right"
      >
        <div style="width: 320px">
          <time-shift-custom-repeat
            ref="timeShiftCustomRepeat"
            :activeDate="activeDate"
            :event="eventModel"
            @save="saveCustomRepeat"
            @cancel="cancelCustomRepeat"
          />
        </div>
      </b-popover>

      <b-row>
        <b-col cols="12" class="d-flex items-center justify-content-end">
          <b-button
            variant="secondary"
            class="close-btn mr-2"
            @click="closeModal"
          >
            {{ $t('button.cancel') }}
          </b-button>
          <n-button-loading
            type="submit"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            :loading="loading"
            :disabled="invalid || !formModel.timeShift"
          >
            {{ $t('button.save') }}
          </n-button-loading>
        </b-col>
      </b-row>
    </n-form-confirmation>
  </validation-observer>
</template>

<script>
  import {
    BPopover,
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BCardTitle,
    BDropdown,
    BDropdownItem
  } from 'bootstrap-vue';
  import FormInput from './updateEvent';
  import FormInput__EndRepeat from './updateEvent__EndRepeat';
  import NButtonLoading from '@/components/NButtonLoading';
  import NFormConfirmation from '@/components/NFormConfirmation';
  import NInput from '@/components/NInput';
  import Ripple from 'vue-ripple-directive';
  import TimeShiftCustomRepeat from './TimeShiftCustomRepeat';
  import TimeShiftForm from '@/components/shares/TimeShiftForm.vue';
  import { formatDate } from '@vueuse/core';
  import cloneDeep from 'lodash/cloneDeep';

  const RRULE_NONE = {
    freq: 'daily',
    interval: 1,
    count: 1
  };

  export default {
    name: 'TimeShiftDropdown',
    components: {
      BPopover,
      BRow,
      BCol,
      BCard,
      BButton,
      BFormGroup,
      BCardTitle,
      BDropdown,
      BDropdownItem,
      NButtonLoading,
      NFormConfirmation,
      NInput,
      TimeShiftCustomRepeat,
      TimeShiftForm
    },
    props: {
      activeDate: {
        type: Date,
        required: true
      },
      event: {
        type: Object,
        default: () => {}
      }
    },
    directives: {
      Ripple
    },
    data() {
      return {
        isReady: false,
        formModel: {
          timeShift: null,
          repeatType: {
            value: 'none',
            name: 'label.none'
          }
        },
        formModel__EndRepeat: {},
        showCustom: false,
        loading: false,
        showTimeShiftModal: false,
        fields: [],

        // Bind final data to this model
        eventModel: {
          title: '',
          rrule: { ...RRULE_NONE },
          isCustom: false,
          startTime: '',
          endTime: '',
          timeShift: {
            id: '',
            title: '',
            timezone: ''
          }
        }
      };
    },
    setup(props) {
      const fields__EndRepeat = FormInput__EndRepeat.map(field => ({
        ...field,
        min: props.activeDate
      }));
      return {
        fields__EndRepeat
      };
    },
    created() {
      this.initData();
    },
    mounted() {
      setTimeout(() => {
        this.isReady = true;
      }, 100);
    },
    computed: {
      locale() {
        return this.$i18n.locale.split('-')[0];
      },
      repeatType() {
        const { repeatType } = this.formModel;
        return repeatType?.value || 'none';
      },
      endRepeat() {
        const { endRepeat } = this.formModel__EndRepeat;
        return endRepeat;
      },
      timeShift() {
        const { timeShift } = this.formModel;
        return timeShift || null;
      },
      displayDate() {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Intl.DateTimeFormat(this.$i18n.locale, options).format(this.activeDate);
      },
      formatedActiveDate() {
        return formatDate(this.activeDate, 'YYYY-MM-DD');
      },
      isNew() {
        return !this.event;
      },
      isRoot() {
        return this.formatedActiveDate == this.event?.rrule?.dtstart;
      },
      isSingle() {
        return this.event?.rrule?.count == 1;
      }
    },
    watch: {
      repeatType: {
        handler(newVal) {
          let resetExdateTrigger = false;

          switch (newVal) {
            case 'none':
              // Reset
              this.eventModel.isCustom = false;
              this.eventModel.rrule = { ...RRULE_NONE };
              this.eventModel.rrule.count = 1; // count must 1

              resetExdateTrigger = this.event?.rrule?.count != 1;

              break;
            case 'custom':
              delete this.eventModel.rrule.count;
              if (!this.eventModel.isCustom) {
                this.showCustom = true;
              }

              resetExdateTrigger = !this.event?.isCustom;

              break;

            default:
              // interval must 1
              // must delete count
              this.eventModel.rrule.freq = newVal;
              this.eventModel.rrule.interval = 1;
              this.eventModel.isCustom = false;
              delete this.eventModel.rrule.count;

              resetExdateTrigger = this.event?.rrule?.freq != newVal;

              break;
          }

          // Reset exdate when repeatType changed from original data
          if (resetExdateTrigger) {
            this.eventModel.exdate = [];
          }
        }
      },
      endRepeat: {
        handler(newVal) {
          this.eventModel.rrule.until = newVal;
        }
      },
      timeShift: {
        handler(newVal) {
          const { startTime, endTime, title } = newVal;

          this.eventModel.title = title;
          this.eventModel.startTime = startTime;
          this.eventModel.endTime = endTime;
          this.eventModel.timeShift = { ...newVal };
        }
      }
    },
    methods: {
      initData() {
        // Prepare form model
        this.fields = FormInput.map(field => {
          return {
            ...field,
            handleAddNew:
              field.key == 'timeShift' ? this.handleAddNewTimeShift : null
          };
        });

        if (this.event) {
          const { title, timeShift, rrule, isCustom } = this.event;

          this.eventModel = cloneDeep(this.event);

          this.$nextTick(() => {
            // Find repeatType
            const repeatTypeValue = isCustom
              ? 'custom'
              : rrule.count == 1
              ? 'none'
              : rrule.freq;

            // Restore formModel
            this.formModel.title = title;
            this.formModel.timeShift = { 
              ...timeShift,
              translations: timeShift.titleTranslate?.[this.locale] || timeShift.titleTranslate?.en || timeShift.titleTranslate?.zh || timeShift.titleTranslate?.km || timeShift.titleTranslate?.vi || timeShift?.title || '-'
            };
            this.formModel.repeatType = {
              ...FormInput.find(e => e.key === 'repeatType').options.find(
                e => e.value === repeatTypeValue
              )
            };

            // Restore endRepeat value
            this.formModel__EndRepeat.endRepeat = rrule.until;
          });
        }
      },
      validateForm() {
        this.$refs.updateEvent.validate().then(success => {
          if (success) {
            this.submit();
          }
        });
      },
      submit() {
        this.$nextTick(() => {
          this.$emit('submit', this.eventModel);
        });
      },
      closeModal() {
        this.$emit('close');
      },
      cancelCustomRepeat() {
        this.showCustom = false;
        if (!this.eventModel.isCustom) {
          this.formModel.repeatType = {
            value: 'none',
            name: 'label.none'
          };
        }
      },
      saveCustomRepeat(data) {
        const oldData = this.eventModel.rrule;

        this.showCustom = false;
        this.eventModel.isCustom = true;
        this.eventModel.rrule = { ...oldData, ...data };
      },
      handleAddNewTimeShift() {
        this.showTimeShiftModal = true;
      },
      onAddedTimeShift(data) {
        this.formModel.timeShift = { ...data };
        this.onTimeShiftModalClose();
      },
      onTimeShiftModalClose() {
        this.showTimeShiftModal = false;
      },
      handleEditCustomRepeat() {
        this.showCustom = true;
      },
      handleDelete() {
        this.$emit('delete');
      },
      handleDeleteOne() {
        if (!this.eventModel.exdate) {
          this.eventModel.exdate = [];
        }

        this.eventModel.exdate.push(this.formatedActiveDate);

        this.submit();
      },
      handleDeleteFrom() {
        const refactoredExdate = [];
        const { exdate } = this.eventModel;
        if (exdate) {
          for (const d of exdate) {
            if (new Date(d) < this.activeDate) {
              refactoredExdate.push(d);
            }
          }

          this.eventModel.exdate = [...refactoredExdate];
        }

        const date = this.activeDate;
        date.setSeconds(-1);

        this.eventModel.rrule.until = formatDate(date, 'YYYY-MM-DD');

        this.submit();
      }
    }
  };
</script>
